import { FileText, Info, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type RegenerateScenariosDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  scenarioTitle: string | null;
  scenarioCount: number;
};

export function RegenerateScenariosDialog({
  open,
  onClose,
  onConfirm,
  scenarioTitle,
  scenarioCount,
}: RegenerateScenariosDialogProps) {
  const replaced = [
    "Les scénarios proposés pour cet artefact",
    "Le scénario retenu et les modifications de texte",
    "L'audio et le diaporama déjà générés",
  ];

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-md gap-0 overflow-hidden border border-[#E2E8F0] p-0">
        <DialogHeader className="flex flex-row items-start gap-4 space-y-0 border-b border-[#F4F4F4] bg-[#F8FAFC] px-6 py-5 text-left">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#FFF4E5] text-[#F59E0B]">
            <RotateCcw className="h-5 w-5" />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5 pr-6">
            <DialogTitle className="text-[18px] font-semibold leading-tight text-[#0F172B]">
              Regénérer les scénarios&nbsp;?
            </DialogTitle>
            <DialogDescription className="text-[14px] leading-5 text-[#45556C]">
              Un scénario a déjà été retenu pour cet artefact. {scenarioCount > 1 ? `${scenarioCount} nouveaux scénarios` : "Un nouveau scénario"} vont être rédigés à partir de la configuration actuelle.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-4 px-6 py-5">
          {scenarioTitle && (
            <div className="flex items-center gap-3 rounded-[12px] border border-[#E2E8F0] bg-white px-4 py-3">
              <FileText className="h-4 w-4 shrink-0 text-[#8EA4BD]" />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[11px] font-medium uppercase tracking-wide text-[#8EA4BD]">Scénario retenu</span>
                <span className="truncate text-[14px] font-semibold text-[#0F172B]" title={scenarioTitle}>
                  {scenarioTitle}
                </span>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <p className="text-[13px] font-semibold text-[#0F172B]">Sera remplacé une fois la génération terminée :</p>
            <ul className="flex flex-col gap-1.5">
              {replaced.map((item) => (
                <li key={item} className="flex items-start gap-2 text-[13px] leading-5 text-[#45556C]">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#F59E0B]" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-start gap-2 rounded-[10px] bg-[#EFF6FF] px-3 py-2.5 text-[13px] leading-5 text-[#1E4E8C]">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#007AFF]" />
            <span>
              Les autres artefacts du projet ne sont pas touchés. Si la génération échoue ou est annulée, cet artefact reste inchangé.
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-[#F4F4F4] bg-[#F8FAFC] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-xl border border-[#E2E8F0] bg-white px-4 text-sm font-medium text-[#0F172B] transition-colors hover:bg-[#F1F5F9]"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#F59E0B] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#D97706]"
          >
            <RotateCcw className="h-4 w-4" />
            Regénérer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
